import { api } from "@/lib/api";

// Firebase Cloud Messaging on the web (and in the Android TWA, which runs this same site in Chrome).
// Config is public (REACT_APP_*), the backend sends with the service account. Uses the existing /sw.js so FCM and
// VAPID notifications share one service worker and one notification tag (no duplicates).
const CFG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const TOKEN_KEY = "evidex.fcm.token";

export const fcmConfigured = () => !!(CFG.apiKey && CFG.projectId && CFG.messagingSenderId && CFG.appId && VAPID);
export const fcmLocalToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };

const supported = async () => {
  if (!fcmConfigured() || !("serviceWorker" in navigator) || !("Notification" in window)) return false;
  const { isSupported } = await import("firebase/messaging");
  return isSupported();
};

const messaging = async () => {
  const [{ initializeApp, getApps }, { getMessaging }] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
  const app = getApps()[0] || initializeApp(CFG);
  return getMessaging(app);
};

const swReg = async () => (await navigator.serviceWorker.getRegistration("/")) || navigator.serviceWorker.register("/sw.js");

export async function enableFcm() {
  if (!(await supported())) throw new Error("Firebase push is not available in this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications were not allowed for this site");
  const { getToken } = await import("firebase/messaging");
  const token = await getToken(await messaging(), { vapidKey: VAPID, serviceWorkerRegistration: await swReg() });
  if (!token) throw new Error("Firebase did not return a device token");
  const r = await api.post("/push/fcm/register", { token, platform: /Android/i.test(navigator.userAgent) ? "android" : "web" });
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* storage blocked */ }
  return { token, devices: r.data.devices };
}

export async function disableFcm() {
  const token = fcmLocalToken();
  let devices = null;
  if (token) {
    try { devices = (await api.delete("/push/fcm/register", { data: { token, platform: "web" } })).data.devices; } catch { /* already gone */ }
    try { const { deleteToken } = await import("firebase/messaging"); await deleteToken(await messaging()); } catch { /* token already invalid */ }
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ }
  }
  return devices;
}

// Foreground messages (tab open): show as a toast via callback; background ones are rendered by /sw.js.
export async function onFcmMessage(cb) {
  if (!(await supported())) return () => {};
  const { onMessage } = await import("firebase/messaging");
  return onMessage(await messaging(), (m) => cb({ ...(m.notification || {}), ...(m.data || {}) }));
}
